import React from 'react';
import { Space, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faCopy, faStar, faTrash, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../../context/ThemeContext';
import Table from '../../../../commonComponents/Table';
import { ViewProps } from '../types.d';
import { TEMPLATE_TABLE_CONSTANTS } from '../constants';
import { getTemplateTypeLabel, getStatusColor } from '../../constants/emailConstants';
import {
  TemplateNameCell,
  TemplateInfo,
  TemplateName,
  TemplateDescription,
  ActionIconButton,
} from '../styles';

const TabletView: React.FC<ViewProps> = ({
  templates,
  templateTypes,
  onEdit,
  onDuplicate,
  onSetDefault,
  onDelete,
  onPreview,
  onTestEmail,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();

  const columns: ColumnsType<any> = [
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.TEMPLATE_NAME,
      key: 'name',
      render: (_, template) => (
        <TemplateNameCell>
          <TemplateInfo>
            <TemplateName>{template.name}</TemplateName>
            {template.description && (
              <TemplateDescription>{template.description}</TemplateDescription>
            )}
          </TemplateInfo>
        </TemplateNameCell>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.TYPE,
      key: 'type',
      width: 120,
      render: (_, template) => (
        <Tag color="blue">{getTemplateTypeLabel(template.type, templateTypes)}</Tag>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.STATUS,
      key: 'status',
      width: 120,
      render: (_, template) => (
        <Tag color={getStatusColor(template.status)}>
          {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
        </Tag>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.DEFAULT,
      key: 'isDefault',
      width: 100,
      render: (_, template) =>
        template.isDefault ? (
          <Tag color="green">
            Default
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.USAGE,
      key: 'usageCount',
      width: 80,
      render: (_, template) => template.usageCount || 0,
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.ACTIONS,
      key: 'actions',
      width: 150,
      align: 'center' as const,
      render: (_, template) => (
        <Space size="small">
          {template.htmlContent && (
            <ActionIconButton onClick={() => onPreview(template)} iconColor="#2D6CCA" title="Preview">
              <FontAwesomeIcon icon={faEye} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
          <ActionIconButton onClick={() => onEdit(template)} iconColor="#2D6CCA">
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
          <ActionIconButton onClick={() => onDuplicate(template)} iconColor="#2D6CCA">
            <FontAwesomeIcon icon={faCopy} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
          {onTestEmail && (
            <ActionIconButton onClick={() => onTestEmail(template)} iconColor="#2D6CCA" title="Send test email">
              <FontAwesomeIcon icon={faPaperPlane} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
          {!template.isDefault && (
            <ActionIconButton onClick={() => onSetDefault(template)} iconColor="#2D6CCA">
              <FontAwesomeIcon icon={faStar} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
          {!template.isDefault && (
            <ActionIconButton onClick={() => onDelete(template)} iconColor="#f5222d">
              <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={templates}
      rowKey="id"
      pagination={false}
      loading={loading}
      emptyText={TEMPLATE_TABLE_CONSTANTS.MESSAGES.NO_TEMPLATES}
      scroll={{ x: true, y: 600 }}
      headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
      removeVerticalBorders={true}
      thPadding="15px 10px"
      tdPadding="10px 10px"
      firstChildBorderRadius="8px"
      border="0"
      borderBottomLeftRight={true}
      theme={isDarkMode ? 'dark' : 'light'}
    />
  );
};

export default TabletView;
